import { Platform } from '@angular/cdk/platform';
import { LowerCasePipe, ViewportScroller } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  ViewEncapsulation,
  afterNextRender,
  computed,
  effect,
  inject,
  input,
  model,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { TranslocoModule, provideTranslocoScope } from '@jsverse/transloco';
import { AutoCompleteResult } from '@rma/generic/api/auto-search';
import { PagedList } from '@rma/generic/domain/types';
import { BrokenImageDirective } from '@rma/generic/ui/broken-image';
import { IconTheme, RmaIconComponent } from '@rma/generic/ui/icon';
import { RmaUrlsService } from '@rma/generic/util/environment';
import { TypeAheadHighlightPipe } from '@rma/generic/util/pipes/typeahead-highlight';
import { SearchType } from '@rma/search/domain';
import { BrowseMode } from '@rma/site/private/util-browse-mode';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs';
import { AutoSearchGroupLabelPipe } from './ui-grouping-label/grouping-label.pipe';

@Component({
  selector: 'rma-search-auto-complete',
  templateUrl: './auto-complete-control.component.html',
  styleUrls: ['./auto-complete-control.component.scss'],
  host: {
    '[class.no-border]': 'noBorder()',
    '[class.rounded-control]': 'rounded()',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [
    AutoSearchGroupLabelPipe,
    BrokenImageDirective,
    InfiniteScrollDirective,
    LowerCasePipe,
    MatAutocompleteModule,
    MatFormFieldModule,
    MatInputModule,
    MatOptionModule,
    MatProgressSpinnerModule,
    ReactiveFormsModule,
    RmaIconComponent,
    RouterLink,
    TranslocoModule,
    TypeAheadHighlightPipe,
  ],
  providers: [provideTranslocoScope('search')],
})
export class AutoCompleteControlComponent {
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly platform = inject(Platform);
  private readonly viewportScroller = inject(ViewportScroller);
  private readonly rmaUrls = inject(RmaUrlsService);

  private readonly autoCompleteRef = viewChild<ElementRef<HTMLInputElement>>('autocomplete');
  private readonly autoComplete = viewChild<MatAutocompleteTrigger, MatAutocompleteTrigger>('autocomplete', {
    read: MatAutocompleteTrigger,
  });

  public readonly scope = 'search';

  public readonly results = model<PagedList<AutoCompleteResult> | null>({ skip: 0, take: 0, total: 0, results: [] });
  public readonly debounce = input(300);
  public readonly minLength = input(1);
  public readonly placeholder = input<string | null>();
  public readonly position = input<'auto' | 'above' | 'below'>('auto');
  public readonly selectedMode = input<BrowseMode | null>();
  public readonly selectedSearchType = input<SearchType | null>(null);
  public readonly autoFocus = input(false);
  public readonly searchByAddressLoading = input<boolean | null>(false);
  public readonly initalValue = input<string | null>('');
  public readonly showLabel = input(true);
  public readonly showPrefix = input(true);
  public readonly prefixTheme = input<IconTheme>();
  public readonly showSuffix = input(false);
  public readonly suffixTheme = input<IconTheme>();
  public readonly disabled = input<boolean | null>(false);
  public readonly showCreateProfile = input(false);
  public readonly noBorder = input(false);
  public readonly rounded = input(false);

  public readonly update = output<string>();
  public readonly selected = output<MatAutocompleteSelectedEvent>();
  public readonly viewedAll = output();
  public readonly opened = output();
  public readonly closed = output();

  public readonly inputControl = new FormControl('', { nonNullable: true });
  public readonly searchTerm$ = this.inputControl.valueChanges.pipe(
    debounceTime(this.debounce()),
    distinctUntilChanged(),
    takeUntilDestroyed(),
  );
  public readonly searchTerm = toSignal(this.searchTerm$, { initialValue: '' });

  public readonly state = computed(() => ({
    results: this.results(),
    loading: signal(false),
    queryParam: signal(false),
  }));

  public readonly template = computed(() =>
    ['Locations', 'StreetAddress'].includes(this.selectedSearchType() ?? 'Locations') ? 'Location' : 'Default',
  );

  public readonly placeholderImage = computed(() => {
    switch (this.selectedSearchType()) {
      case 'Agencies':
        return this.rmaUrls.imageUrl('placeholder/agency.jpg');
      case 'Agents':
        return this.rmaUrls.imageUrl('placeholder/agent.jpg');
      default:
        return '';
    }
  });

  public readonly autcompleteClass = computed(() => {
    return `search-results-panel ${this.noBorder() ? 'no-border' : ''}`;
  });

  public readonly hasMoreResults = computed(() => {
    const results = this.state().results;

    if (results) {
      return results.skip + results.take < results.total;
    }

    return false;
  });

  constructor() {
    const validInput$ = this.searchTerm$.pipe(filter((res: string) => res.length >= this.minLength()));
    const invalidInput$ = this.searchTerm$.pipe(filter((res: string) => res.length < this.minLength()));

    validInput$.pipe(takeUntilDestroyed()).subscribe((value: string) => {
      this.state().loading.set(true);
      this.update.emit(value);
    });
    invalidInput$.pipe(takeUntilDestroyed()).subscribe(() => this.onClear());

    // close auto complete when no search term
    effect(() => {
      if (!this.searchTerm() && this.autoComplete()?.panelOpen) {
        this.autoComplete()?.closePanel();
      }
    });

    // switching between search types set input focus
    effect(() => {
      if (this.selectedSearchType() && this.autoCompleteRef()) {
        // wait for search to be performed
        setTimeout(() => {
          this.setFocus(this.autoFocus());
        }, this.debounce());
      }
    });

    // switch mode reset search term
    effect(() => {
      if (this.selectedMode()) {
        untracked(() => {
          if (!this.state().queryParam()) {
            this.inputControl.reset();
          }
        });
      }
    });

    // enable / disable search control
    effect(() => {
      const disabled = this.disabled();
      if (disabled) {
        this.inputControl.disable({ emitEvent: false });
      } else {
        this.inputControl.enable({ emitEvent: false });
      }
    });

    // set initial value but don't perform the search, onFocus() method will handle that
    effect(() => {
      const initialValue = this.initalValue() ?? '';

      if (initialValue) {
        setTimeout(() => this.inputControl.setValue(initialValue, { emitEvent: false }), this.debounce());
      }
    });

    afterNextRender(() => this.applyQueryParamSearch());
  }

  public onOptionSelected(event: MatAutocompleteSelectedEvent): void {
    this.selected.emit(event);
  }

  public displayFn(result: AutoCompleteResult): string {
    return typeof result === 'string' ? result : result && result.name ? result.name : '';
  }

  public onClear() {
    this.inputControl.reset('');
    this.results.set(null);
    this.update.emit('');
    this.setFocus();
  }

  /**
   * Initial value has been set but we don't want auto search to fire until we focus on the input
   */
  public onFocus() {
    if (this.inputControl.value && !this.results()?.total) {
      this.inputControl.setValue(this.inputControl.value);
    }
  }

  public onScrolled() {
    if (this.hasMoreResults()) {
      this.viewedAll.emit();
    }
  }

  private setFocus(focus = true) {
    const autoCompleteRef = this.autoCompleteRef();
    if (focus && autoCompleteRef) {
      autoCompleteRef.nativeElement.focus({
        preventScroll: true,
      });

      // focus-options-polyfill not working as expected
      if (this.platform.SAFARI) {
        setTimeout(() => {
          this.viewportScroller.scrollToPosition([0, 0]);
        });
      }
    }
  }

  private applyQueryParamSearch() {
    const searchParameter = this.activatedRoute.snapshot.queryParamMap.get('q');

    if (!searchParameter) {
      return;
    }

    this.state().queryParam.set(true);
    this.inputControl.setValue(searchParameter);
  }
}
